import { HETEROZYGOUS_2, HETEROZYGOUS_1 } from "../../constants/zygosity";
import { REPEAT_EXPANSION } from "../../constants/structuralVariants";

const REJECT_ZYGOCITY_VALS = new Set([-1, "-1", 0, "0"]);

// heterozygous2 should be handled as heterozygous1 (no valid difference)
function normalizeZygosity(zygValue) {
  if (zygValue === HETEROZYGOUS_2) return HETEROZYGOUS_1;
  return zygValue;
}

// make the repeat expansion data look like the rest of structural variants
function normalizeRepeatExpansion(repexp) {
  const { chromo, genes, position, classification, sample_repeats } = repexp;
  const sampleRepeats = Number(sample_repeats);
  const repeatUnit = repexp.repeat_unit;
  const unitLength = repeatUnit.length;
  const minPathoRepeats = repexp.min_pathogenic_repeats;

  return {
    sampleRepeats,
    repeatUnit,
    minPathoRepeats,
    acmg_class: classification,
    position,
    genes,
    chromo,
    length: unitLength * sampleRepeats,
    type: REPEAT_EXPANSION,
  };
}

function normalizeAnalysis(data) {
  const resRepExp = data.res || [];
  const resCNVs = data.cnvs || [];
  const repExps = resRepExp.map(normalizeRepeatExpansion).filter(Boolean);
  const variants = resCNVs.concat(repExps);
  const analysisData = {
    key: `${data.analysis_id}-${data.sample_name}`,
    createdAt: new Date(data.date_created),
    id: data.analysis_id,
    parentAnalysisId: data.parent_analysis_id,
    type: data.type,
    sampleName: data.sample_name,
    analysisUrl: data.analysis_url,
  };

  const vars = variants
    .filter(filterValidSV)
    .map((y) => normalizeStructVar(y, analysisData));

  return { ...analysisData, variants: vars };
}

function filterValidSV(data) {
  return data && !REJECT_ZYGOCITY_VALS.has(data?.zygosity);
}

function normalizeStructVar(sv, analysis) {
  const dataPerAnalysis = {
    sample: analysis.sampleName,
    createdAt: analysis.createdAt,
    analysis: analysis.id,
    copyNumber: sv.copy_number,
    zygosity: normalizeZygosity(sv.zygosity),
    failsAll: sv.fails_all_quality_checks,
    passesAll: sv.passes_all_quality_checks,
  };

  return {
    ...sv,
    id: `${sv.chromo}-${sv.position}-${sv.length}-${sv.type}`,
    analysisDict: { [analysis.key]: dataPerAnalysis },
    inAnalyses: new Set([analysis.id]),
    inSamples: new Set([analysis.sampleName]),
    analysisId: analysis.id,
    sampleName: analysis.sampleName,
  };
}

function findEqualsToOtherSamples(dupSVs) {
  const svDict = new Map();

  for (const sv of dupSVs) {
    const existing = svDict.get(sv.id) || [];

    const updated = [...existing, sv];

    existing.forEach((x) => {
      sv.inAnalyses.add(x.analysisId);
      sv.inSamples.add(x.sampleName);
      sv.analysisDict = { ...sv.analysisDict, ...x.analysisDict };
    });

    updated.forEach((x) => {
      x.inAnalyses.add(sv.analysisId);
      x.inSamples.add(sv.sampleName);
      x.analysisDict = { ...x.analysisDict, ...sv.analysisDict };
    });

    svDict.set(sv.id, updated);
  }
}

function processResponse(res) {
  const data = (Array.isArray(res) ? res : []).filter(Boolean);
  const normAnalyses = data.map(normalizeAnalysis);
  const analysisMap = normAnalyses.reduce((a, i) => a.set(i.key, i), new Map());

  const allSVs = data
    .map(normalizeAnalysis)
    .map((normAnalysis) => normAnalysis.variants)
    .flat();

  findEqualsToOtherSamples(allSVs);

  const analysesArr = Array.from(analysisMap.values());
  const sampleNames = new Set(analysesArr.map((x) => x.sampleName));

  const cnvs = sampleNames
    .map((name) => ({
      name,
      cnvs: allSVs.filter((x) => x.sampleName === name),
      analyses: analysesArr.filter((x) => x.sampleName === name),
    }))
    .reduce((a, i) => ({ ...a, [i.name]: i }), {});

  return { cnvs, analysisDict: analysisMap };
}

export default processResponse;
